import { ExpandMore, InfoOutlined } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Paper, Tooltip, Typography } from '@mui/material';
import { t } from 'i18next';
import { useState } from 'react';
import { ProjectDetailsController } from '../../../common/dashboards/controllers/ProjectDetailsController';
import { Severity } from '../../../common/enums/Severity';
import { Project } from '../models/Project';
import { Issues as IssuesSkeleton } from './skeletons/Issues';

type Props = {
  loading: boolean;
  project: Project | null;
  withFilteredView: boolean;
  defaultOpenForSeverities?: Severity[];
  focusOnIssueNumber?: number | null;
};

const SEVERITY_GROUPS = [
  {
    severities: [Severity.HIGH, Severity.MED, Severity.LOW],
    label: () => t('priorityIssues'),
    tooltip: () => t('severity.tooltip.priority'),
    defaultOpen: true,
  },
  { severities: [Severity.PASSED], label: () => t('passedIssues'), tooltip: () => t('severity.tooltip.passed'), defaultOpen: false },
  {
    severities: [Severity.DISABLED],
    label: () => t('disabledIssues'),
    tooltip: () => t('severity.tooltip.disabled'),
    defaultOpen: false,
  },
  {
    severities: [Severity.FILTERED],
    label: () => t('filteredIssues'),
    tooltip: () => t('severity.tooltip.filtered'),
    defaultOpen: false,
  },
  {
    severities: [Severity.NA],
    label: () => t('naIssues'),
    tooltip: () => t('severity.tooltip.NA'),
    defaultOpen: false,
  },
];

export const Issues = ({ project, loading, withFilteredView, defaultOpenForSeverities = [], focusOnIssueNumber = null }: Props) => {
  const [openGroups, setOpenGroups] = useState<boolean[]>(
    SEVERITY_GROUPS.map(({ severities, defaultOpen }) => severities.filter((severity) => defaultOpenForSeverities.includes(severity)).length > 0 || defaultOpen)
  );

  if (loading) {
    return <IssuesSkeleton />;
  }

  if (project === null) {
    return null;
  }

  const handleGroupOpen = (index: number, open: boolean) => {
    setOpenGroups((groups) => {
      const newOpenGroups = [...groups];
      newOpenGroups[index] = open;
      return newOpenGroups;
    });
  };

  const date = project.getLastAnalysis()?.fromNow() || t('noAnalysisYet');

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" component="h2" sx={{ fontSize: '2.125rem' }}>
          {t('issues')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" component="p" sx={{ color: 'text.secondary' }}>
            {t('projectDashboardIssuesSubtitle')}
          </Typography>
          <Typography variant="subtitle1" component="p" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {t('lastTraceAnalyzed', { date })}
          </Typography>
        </Box>
        {SEVERITY_GROUPS.map((g, index) => (
          <Accordion
            expanded={openGroups[index]}
            onChange={(event, expanded) => handleGroupOpen(index, expanded)}
            key={index}
            slotProps={{ transition: { unmountOnExit: true } }}
            sx={{ mt: 2, '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              id={`issue-panel-${index}-header`}
              aria-controls={openGroups[index] ? `issue-panel-${index}-content` : undefined}
              expandIcon={<ExpandMore />}
              title={t('a11y.project.dashboard.issueAccordion', { severity: g.label() })}
              sx={{ backgroundColor: 'grey.100', fontWeight: '700', px: 0, flexDirection: 'row-reverse', '& svg': { mx: 1.5 } }}
            >
              {g.label()}
              <Tooltip title={g.tooltip()} placement="top" arrow>
                <InfoOutlined sx={{ color: 'grey.600' }} fontSize="small" />
              </Tooltip>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: 'backdrops.expandedRow', p: 2 }}>
              <Paper>
                <ProjectDetailsController
                  severities={g.severities}
                  project={project}
                  withFilteredView={withFilteredView}
                  focusOnIssueNumber={focusOnIssueNumber}
                />
              </Paper>
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>
    </Card>
  );
};
